import { useState, useEffect, startTransition } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, CircularProgress, Typography, Box,
  Table, TableBody, TableRow, TableCell, TableHead,
  Chip,
} from '@mui/material';
import type { Study, PatientBean, PatientDiscoveryResult } from '../../api/types';
import { api } from '../../api/rpc';

interface PatientDiscoveryDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

export const PatientDiscoveryDialog = ({ open, study, onClose }: PatientDiscoveryDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PatientDiscoveryResult | null>(null);

  useEffect(() => {
    if (!open || !study) return;
    startTransition(() => { setLoading(true); setError(null); setResult(null); });

    const patKey = study.patientKey ?? study.objKey;
    api.findPatientsDiscovery(patKey)
      .then(r => setResult(r))
      .catch(e => setError(e instanceof Error ? e.message : 'Patient discovery failed'))
      .finally(() => setLoading(false));
  }, [open, study]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Patient Discovery</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              Searching for matches for <strong>{study.name}</strong>
              {study.id ? ` (ID: ${study.id})` : ''}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Searching local and remote patient sources…
            </Typography>
          </Box>
        ) : result ? (
          <>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`${result.total} match${result.total !== 1 ? 'es' : ''} found`}
                color={result.total > 0 ? 'success' : 'default'}
                size="small"
              />
              {result.errors && result.errors.length > 0 && (
                <Chip
                  label={`${result.errors.length} error${result.errors.length !== 1 ? 's' : ''}`}
                  color="warning"
                  size="small"
                />
              )}
            </Box>

            {result.errors && result.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {result.errors.map((err, i) => (
                  <Typography key={i} variant="body2">{err}</Typography>
                ))}
              </Alert>
            )}

            {result.patients.length > 0 ? (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>DOB</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sex</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Site</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.patients.map((p: PatientBean, i: number) => (
                      <TableRow
                        key={p.objKey ?? i}
                        hover
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{p.niceName ?? p.name}</TableCell>
                        <TableCell>{p.id ?? 'N/A'}</TableCell>
                        <TableCell>{p.dob ?? 'N/A'}</TableCell>
                        <TableCell>{p.sex ?? 'N/A'}</TableCell>
                        <TableCell>{p.siteName ?? p.site ?? 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No matching patients found.
              </Typography>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
